import axios, { AxiosError, AxiosInstance } from "axios";
import config from "./config";
import { TOKEN_EXPIRED, UNAUTHORIZED } from "@common/types/customHTTPCode.ts";
// use sentry logger
import * as Sentry from '@sentry/react';
import { API_VERSION_HEADER, EXPIRATION_HEADER } from "@common/commonConstants.ts";
import { IgnoreCanceledError, NotAuthenticatedError, UnexpectedAuthenticateError } from "./customErrors.ts";
import { clearPersistedBuyersFilters } from "../hooks/usePersistedBuyersFilters.ts";
const { logger } = Sentry;

export class AxiosProvider {
    // expiration in ms from epoch
    expiration_ms: number | null = null;
    version: string | null = null
    listeners: Array<(expiration: number | null) => void> = [];
    versionListeners: Array<(version: string | null) => void> = [];

    // Adds a listener function to the list of listeners
    addListener(listener: (expiration: number | null) => void): void {
        if (typeof listener === 'function') {
            this.listeners.push(listener);
        }
    }

    // Removes a listener function from the list of listeners
    removeListener(listener: (expiration: number | null) => void): void {
        this.listeners = this.listeners.filter(fn => fn !== listener);
    }

    notifyListeners(): void {
        this.listeners.forEach(callback => callback(this.expiration_ms));
    }

    addVersionListener(listener: (version: string | null) => void): void {
        this.versionListeners.push(listener);
    }

    removeVersionListener(listener: (version: string | null) => void): void {
        this.versionListeners = this.versionListeners.filter(fn => fn !== listener);
    }

    notifyVersionListeners(): void {
        this.versionListeners.forEach(callback => callback(this.version));
    }

    // Implement your logic to retrieve the token from storage (e.g., localStorage or cookies)
    getApi(_ = true): AxiosInstance {
        const axiosConfig = { baseURL: config.serverBaseUrl(window.location.origin), withCredentials: true };
        const instance = axios.create(axiosConfig);
        const preSendExpiration = this.expiration_ms;

        instance.interceptors.request.use(
            (config) => {
                // copy back information we have to the server
                config.headers["x-client-info"] = localStorage.getItem("login_info");
                config.headers["x-client-expiration"] = this.expiration_ms?.toString() ?? "";
                return config;
            },
            async (error) => {
                return await Promise.reject(error);
            }
        );
        instance.interceptors.response.use(
            (res) => {
                const expiration = res.headers[EXPIRATION_HEADER] as string | undefined;

                if (expiration != null && expiration !== "") {
                    const newExpiration = parseInt(expiration);
                    if (newExpiration !== this.expiration_ms) {
                        this.expiration_ms = newExpiration;
                        this.notifyListeners();
                    }
                }
                const newVersion = res.headers[API_VERSION_HEADER] as string | undefined;
                if (newVersion != null && newVersion !== "") {
                    if (newVersion !== this.version) {
                        this.version = newVersion;
                        this.notifyVersionListeners();
                    }
                }

                return res;
            },
            async (err) => {
                if (err instanceof AxiosError) {
                    // for now handle both token expired and unauthorized the same
                    if (err.response?.status === TOKEN_EXPIRED) {
                        this.onExpired();
                        // sentry logger activity filters specific text words from logs
                        logger.warn(`user expired navigate to login,  expires=${this.expiration_ms}`);
                        window.location.href = '/login';
                        if (preSendExpiration != null && preSendExpiration > Date.now()) {
                            // frontend and backend disagree on the expiration
                            throw new UnexpectedAuthenticateError(err, preSendExpiration, err.response?.status, window.location.pathname, err.config?.url);
                        } else {
                            throw new NotAuthenticatedError(err, preSendExpiration, err.response?.status, window.location.pathname, err.config?.url);
                        }
                    } else if (err.response?.status === UNAUTHORIZED && (window.location.pathname.startsWith('/a/') || window.location.pathname.startsWith('/b/'))) {
                        logger.warn(`no login credentials, navigate to login`);
                        window.location.href = '/login';
                        if (preSendExpiration != null && preSendExpiration > Date.now()) {
                            // frontend and backend disagree on the expiration
                            throw new UnexpectedAuthenticateError(err, preSendExpiration, err.response?.status, window.location.pathname, err.config?.url);
                        } else {
                            throw new NotAuthenticatedError(err, preSendExpiration, err.response?.status, window.location.pathname, err.config?.url);
                        }
                    } else if (err.name === "CanceledError") {
                        // will filter out canceled errors
                        throw new IgnoreCanceledError(err);
                    } else {
                        console.debug(`unexpected error ${err.message} status=${err.response?.status}, url=${err.config?.url}`, { err });
                    }
                }
                return await Promise.reject(err);
            }
        );
        return instance;
    }

    /**
     * Removes the token and the saved product from cookies and localStorage
     */
    async logout(): Promise<void> {
        await this.getApi().post('/api/authenticate/logout').finally(() => {
            this.onExpired();
        });
    }

    onExpired(): void {
        this.expiration_ms = null;
        this.notifyListeners();
        // Clear persisted buyers filters on logout
        clearPersistedBuyersFilters();
    }
}

export const authProvider = new AxiosProvider();
